import {
  COMPANION_PANEL_ID,
  focusCompanionComposer,
  focusCompanionTrigger,
} from '@/components/companion/feed/companion-panel-ids';
import { CompanionPanelHost } from '@/components/companion/feed/CompanionPanelHost';
import { useArchiveCompanionPanelConversation } from '@/hooks/actions/use-archive-companion-panel-conversation';
import { useCompanionActions } from '@/hooks/actions/use-companion-actions';
import {
  useCompanionPanelAsk,
  type CompanionPanelAskNotice,
} from '@/hooks/actions/use-companion-panel-ask';
import {
  companionPanelKeys,
  isCompanionPanelUnavailable,
  useCompanionPanelConversationQuery,
} from '@/hooks/queries/use-companion-panel-conversation-query';
import { ErrorCode } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCompanionFeedQuery } from '@/hooks/queries/use-companion-feed-query';
import { useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { CompanionPanelComposer } from './CompanionPanelComposer';
import { CompanionPanelShell } from './CompanionPanelShell';
import { CompanionPanelThread } from './CompanionPanelThread';
import { useCompanionPage } from './use-companion-page';
import { useCompanionPanelHotkey } from './use-companion-panel-hotkey';
import { useFollowPanelMode, useOpenCompanionPanel } from './use-open-companion-panel';

/**
 * Where the conversational panel lives in the shell, for the cohort of the
 * `COMPANION_PANEL` flag. Unlike the feed panel it does not need the feed: it
 * opens for every brand of the cohort, and the feed only adds the daily view
 * it counts.
 */

interface CompanionPanelShellHostProps {
  userId: string | null | undefined;
  brandId: string | null | undefined;
  /** Top bar plus any banner stacked above it. */
  topOffset: number;
  /** Full-bleed routes (the editors) hide the whole shell, this included. */
  hidden?: boolean;
}

export const CompanionPanelShellHost = ({
  userId,
  brandId,
  topOffset,
  hidden = false,
}: CompanionPanelShellHostProps) => {
  const { isOpen, mode, toggle, close } = useOpenCompanionPanel();
  useFollowPanelMode();
  const page = useCompanionPage();
  const { data: feed } = useCompanionFeedQuery(brandId ?? '');
  const { recordPanelView } = useCompanionActions(brandId ?? '');
  const panelAsk = useCompanionPanelAsk();
  const { data: conversation, error, isPending } = useCompanionPanelConversationQuery(
    brandId ?? ''
  );
  const archive = useArchiveCompanionPanelConversation(brandId ?? '');
  const queryClient = useQueryClient();
  // Closed for the organization (the server kill switch), the cohort is handed
  // back to the feed panel below. Until the panel has answered once, no panel
  // and no shortcut.
  const unavailable = isCompanionPanelUnavailable(error);
  const available = !hidden && !!userId && !!brandId && !unavailable && !isPending;

  // Closing from inside the panel gives the keyboard back to the trigger, once
  // the close is committed (full screen keeps the trigger inert until then).
  const toggleFromKeyboard = useCallback(() => {
    const focusInPanel = !!document.activeElement?.closest(`#${COMPANION_PANEL_ID}`);
    flushSync(toggle);
    if (focusInPanel) focusCompanionTrigger();
  }, [toggle]);
  useCompanionPanelHotkey(toggleFromKeyboard, available);

  // Only an opening takes the keyboard: never a panel already open when the
  // host mounts, nor one that reappears after a full-bleed route.
  const wasOpenRef = useRef(isOpen);
  const justOpened = isOpen && !wasOpenRef.current;
  useEffect(() => {
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Opening reads the conversation afresh, unless an answer is being followed
  // (the stream or the poll already keeps it current).
  const isAnsweringRef = useRef(panelAsk.isAnswering);
  isAnsweringRef.current = panelAsk.isAnswering;
  useEffect(() => {
    if (!isOpen || !brandId || isAnsweringRef.current) return;
    void queryClient.invalidateQueries({ queryKey: companionPanelKeys.conversation(brandId) });
  }, [isOpen, brandId, queryClient]);

  // One daily-usage view per opening, and only where the feed endpoint that
  // records it answers for this brand.
  const feedAvailable = feed?.availability.enabled ?? false;
  const recordPanelViewRef = useRef(recordPanelView);
  recordPanelViewRef.current = recordPanelView;
  const viewRecordedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      viewRecordedRef.current = false;
      return;
    }
    if (!feedAvailable || viewRecordedRef.current) return;
    viewRecordedRef.current = true;
    recordPanelViewRef.current();
  }, [isOpen, feedAvailable]);

  if (unavailable) {
    return (
      <CompanionPanelHost userId={userId} brandId={brandId} topOffset={topOffset} hidden={hidden} />
    );
  }
  if (!available || !isOpen || !mode) return null;

  const firstQuestion = conversation?.turns[0]?.question;
  const archiveRefused =
    isAxiosError(archive.error) &&
    archive.error.response?.data?.code === ErrorCode.E_COMPANION_PANEL_TURN_IN_PROGRESS;
  const notice: CompanionPanelAskNotice | null =
    panelAsk.notice ?? (archiveRefused ? { kind: 'in_progress', question: '' } : null);
  const canStartOver = !!firstQuestion && !panelAsk.isAnswering && !archive.isPending;

  return (
    <CompanionPanelShell
      mode={mode}
      title={firstQuestion ?? 'Companion'}
      mood={panelAsk.isAnswering ? 'look' : 'happy'}
      top={topOffset}
      body={
        <CompanionPanelThread
          key={`${userId}:${brandId}`}
          brandId={brandId ?? ''}
          onRetry={panelAsk.ask}
          isAnswering={panelAsk.isAnswering}
        />
      }
      composer={
        <CompanionPanelComposer
          key={`${userId}:${brandId}`}
          {...panelAsk}
          notice={notice}
          ask={(question) => {
            archive.reset();
            panelAsk.ask(question);
          }}
          pageName={page.name || undefined}
          autoFocus={justOpened}
        />
      }
      onClose={close}
      onNewConversation={
        canStartOver
          ? () => {
              // The pencil leaves with the conversation: the keyboard goes where the next question is typed.
              focusCompanionComposer();
              archive.mutate();
            }
          : undefined
      }
    />
  );
};
